import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import UserView from './views/UserView';
import AdminView from './views/AdminView';
import PublicTimelineView from './views/PublicTimelineView';
import PublicShareView from './views/PublicShareView';
import { Header } from './components/Header';
import { GenerationHistoryModal } from './components/modals/GenerationHistoryModal';
import { GoodsCreationHistoryModal } from './components/modals/GoodsCreationHistoryModal';
import { PaymentHistoryModal } from './components/modals/PaymentHistoryModal'; // Added
import { DirectionSelectionModal } from './components/modals/DirectionSelectionModal';
import ReferenceSettingsView from './views/ReferenceSettingsView'; // 参照設定ビュー
import { ToastNotification } from './components/ToastNotification'; // Added
import { ShareView } from './views/ShareView';
import AwsSmsLoginModal from './components/modals/AwsSmsLoginModal'; // AWS SMS authentication
import { suzuriApiClient, SuzuriGoodsHistoryItem } from './services/suzuriApi';
import {
  User,
  Plan,
  GeneratedImage,
  GoodsCreationRecord,
  GenerationOptions,
  SuzuriItem,
  SharePageParams,
  ActionAfterLoadType,
  AppViewMode,
  AspectRatio,
  AnchorPosition,
  PersonalUserSettings,
  MenuExecutionFormData,
} from './types';
import {
  EXTEND_IMAGE_CREDIT_COST,
  DEFAULT_GENERATION_OPTIONS,
  DEFAULT_ASPECT_RATIO,
} from './constants';
import { ChargeOptionsProvider } from './contexts/ChargeOptionsContext';
import { ChargeOptionsModal } from './components/modals/ChargeOptionsModal';
import { useToast } from './contexts/ToastContext';
import { CategoriesProvider } from './contexts/CategoriesContext';
import { MenusProvider } from './contexts/MenusContext';
import { MasterReferencesProvider } from './contexts/MasterReferencesContext';
import { useCredits, useCreditsActions } from './contexts/CreditsContext';
import { consumeCredits } from './services/api/credits';
import { myGarageLogin, myGarageLogout, validateMyGarageToken, mygarageSsoVerify } from './services/api/mygarage-auth';
import { validatePhoneToken, phoneLogout } from './services/api/phone-auth';
import { 
  fetchTimeline, 
  saveToTimeline, 
  updateTimelineEntry, 
  deleteFromTimeline, 
  fetchPublicTimeline 
} from './services/api/library';
import { expandImage } from './services/api/image-expansion';
import MenuLandingPage from './views/MenuLandingPage';
import { fetchMenuById } from './services/api/menus';
import { fetchCategories } from './services/api/categories';
import { AdminGenerationMenuItem } from './types';
import { SparklesIcon } from './components/icons/HeroIcons';
import { MenuExecutionPanel } from './components/panels/MenuExecutionPanel';

interface AppProps {
  menuIdFromUrl?: number;
}

const App: React.FC<AppProps> = ({ menuIdFromUrl }) => {
  const { showToast } = useToast();

  const credits = useCredits();
  const { refreshCredits } = useCreditsActions();

  const [isAdminView, setIsAdminView] = useState(false);
  const [user, setUser] = useState<User | null>(null); // Initial state set to null, login provides sample user
  const [inputLoginUsername, setInputLoginUsername] = useState('');
  const [inputLoginPassword, setInputLoginPassword] = useState('');
  const [saveLoginPassword, setSaveLoginPassword] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showPersonalSettingsModal, setShowPersonalSettingsModal] =
    useState(false); // Added
  const [showPhoneLoginModal, setShowPhoneLoginModal] = useState(false); // Added
  const [showAwsSmsLoginModal, setShowAwsSmsLoginModal] = useState(false); // AWS SMS Login Modal

  const [generationHistory, setGenerationHistory] = useState<GeneratedImage[]>(
    [],
  );
  const [goodsCreationHistory, setGoodsCreationHistory] = useState<
    GoodsCreationRecord[]
  >([]);
  const [showGenerationHistoryModal, setShowGenerationHistoryModal] =
    useState(false);
  const [showGoodsHistoryModal, setShowGoodsHistoryModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false); // Added
  // 「生成用パネルに読み込ませたい入力パラメータ（menuId, prompt, 画像ファイルなど）」をAppレベルで一時的に保持している変数
  const [menuExePanelFormData, setMenuExePanelFormData] =
    useState<MenuExecutionFormData>({
      category: null,
      menu: null,
      image: null,
      additionalPromptForMyCar: '',
      additionalPromptForOthers: '',
      aspectRatio: DEFAULT_ASPECT_RATIO,
      promptVariables: [],
      inputType: 'upload',
    });
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [optionsToLoadInPanel, setOptionsToLoadInPanel] =
    useState<GenerationOptions | null>(null);
  const [shareParams, setShareParams] = useState<SharePageParams | null>(null);
  const [actionAfterLoad, setActionAfterLoad] =
    useState<ActionAfterLoadType>(null);

  const [allPublicImages, setAllPublicImages] = useState<GeneratedImage[]>([]);

  // URLからのメニューID関連のstate
  const [menuFromUrl, setMenuFromUrl] = useState<AdminGenerationMenuItem | null>(null);
  const [filteredPublicImages, setFilteredPublicImages] = useState<GeneratedImage[]>([]);
  const [isMenuUrlMode, setIsMenuUrlMode] = useState(false);
  // 埋め込み(WebView/iframe)モード: ?embed=1 でヘッダー/ナビを隠し「メニューの中身だけ」表示
  const [isEmbed] = useState<boolean>(() => {
    try {
      return new URLSearchParams(window.location.search).get('embed') === '1';
    } catch {
      return false;
    }
  });

  // 認証チェック中フラグ（トークン検証が完了するまでtrue）
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  // ライブラリから拡張時のローディング状態
  const [isLibraryExtending, setIsLibraryExtending] = useState(false);
  const [currentAppView, setCurrentAppView] =
    useState<AppViewMode>('generator');

  // 拡張方向選択モーダル関連のstate
  const [isDirectionModalOpen, setIsDirectionModalOpen] = useState(false);
  const [imageToExpand, setImageToExpand] = useState<GeneratedImage | null>(null);

  // ユーザー名復元の初期化（パスワードはブラウザ自動入力に任せる）
  useEffect(() => {
    const savedUsername = localStorage.getItem('mygarage-saved-username');
    if (savedUsername) {
      setInputLoginUsername(savedUsername);
      setSaveLoginPassword(true);
    }
    // 旧バージョンで保存されたパスワードを削除
    localStorage.removeItem('mygarage-saved-password');
  }, []);

  // 初期化時のトークン検証
  useEffect(() => {
    const validateTokens = async () => {
      setIsAuthChecking(true);
      // まずMyGarageトークンを確認
      try {
        const myGarageUser = await validateMyGarageToken();
        if (myGarageUser) {
          const aishaUser: User = {
            id: myGarageUser.id,
            name: myGarageUser.name,
            isAdmin: myGarageUser.isAdmin,
            personalSettings: myGarageUser.personalSettings,
            loginType: 'mygarage',
          };
          setUser(aishaUser);
          setCurrentAppView('generator');
          setIsAuthChecking(false);
          return; // MyGarageでログイン成功したら終了
        }
      } catch (error) {
        console.error('MyGarage token validation error:', error);
      }

      // MyGarageでログインできなかった場合、電話番号トークンを確認
      try {
        const phoneUser = await validatePhoneToken();
        if (phoneUser) {
          const aishaUser: User = {
            id: phoneUser.id,
            name: phoneUser.name,
            isAdmin: false,
            personalSettings: null,
            loginType: 'phone',
          };
          setUser(aishaUser);
          setCurrentAppView('generator');
        }
      } catch (error) {
        console.error('Phone token validation error:', error);
      }
      setIsAuthChecking(false);
    };

    validateTokens();
  }, []);

  // URLからのmenuId処理（認証チェック完了後に実行）
  useEffect(() => {
    // 認証チェック中は処理しない
    if (isAuthChecking) return;

    const loadMenuFromUrl = async () => {
      if (menuIdFromUrl) {
        setIsMenuUrlMode(true);
        // メニュー情報を取得
        const menuData = await fetchMenuById(menuIdFromUrl);
        if (menuData) {
          setMenuFromUrl(menuData);
          // フィルタリングされた公開タイムラインを取得
          const filteredImages = await fetchPublicTimeline(menuIdFromUrl);
          setFilteredPublicImages(filteredImages);
          // ログイン済みの場合
          if (user) {
            // タイムラインビューに切り替え
            setCurrentAppView('timeline');
            // カテゴリを取得してメニューと一緒に設定
            const categories = await fetchCategories();
            const category = categories.find(c => c.id === menuData.categoryId) || null;
            // 生成メニューを自動選択（カテゴリも一緒に設定）
            setMenuExePanelFormData((prev) => ({
              ...prev,
              category: category,
              menu: menuData,
            }));
          }
        }
      } else {
        setIsMenuUrlMode(false);
        setMenuFromUrl(null);
        setFilteredPublicImages([]);
      }
    };

    loadMenuFromUrl();
  }, [menuIdFromUrl, user, isAuthChecking]);

  // メニューURLモード時にページタイトルとdescriptionを設定
  useEffect(() => {
    if (menuFromUrl) {
      document.title = `AISHA ${menuFromUrl.name} - by MyGarage`;

      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', menuFromUrl.description || `${menuFromUrl.name} - AIを使った車画像生成サービス AISHA`);
    } else {
      document.title = 'AISHA by MyGarage';
    }
  }, [menuFromUrl]);

  // ライブラリデータの読み込み
  useEffect(() => {
    if (user) {
      loadLibraryData();
      loadPublicLibraryData();
      loadUserGenerationHistory(); // ユーザーの全生成履歴を読み込み
    }
  }, [user]);

  const loadLibraryData = async () => {
    if (!user?.id) return;
    try {
      // ライブラリ保存済みの画像のみ取得
      const libraryData = await fetchTimeline(user.id, true);
      setGenerationHistory(libraryData);
    } catch (error) {
      console.error('ライブラリの読み込みに失敗しました:', error);
      showToast('error', 'ライブラリの読み込みに失敗しました');
    }
  };

  const loadUserGenerationHistory = async () => {
    if (!user?.id) return;
    try {
      // ユーザーの全ての生成履歴を取得（右側パネル表示用）
      const allUserImages = await fetchTimeline(user.id, false);
      setGeneratedImages(allUserImages);
    } catch (error) {
      console.error('生成履歴の読み込みに失敗しました:', error);
      showToast('error', '生成履歴の読み込みに失敗しました');
    }
  };

  const loadPublicLibraryData = async () => {
    try {
      const publicData = await fetchPublicTimeline();
      setAllPublicImages(publicData);
    } catch (error) {
      console.error('公開ライブラリの読み込みに失敗しました:', error);
    }
  };

  useEffect(() => {
    // MyGarageトークン検証でログイン状態復元
    const checkLoginStatus = async () => {
      // MyGarage→AISHA SSO: 署名URL(mg_uid/ts/sig)があれば先に検証してクッキー発行
      try {
        const ssoParams = new URLSearchParams(window.location.search);
        const mgUid = ssoParams.get('mg_uid');
        const ssoTs = ssoParams.get('ts');
        const ssoSig = ssoParams.get('sig');
        if (mgUid && ssoTs && ssoSig) {
          await mygarageSsoVerify(mgUid, ssoTs, ssoSig);
          // 署名パラメータをURL(履歴)から除去
          ssoParams.delete('mg_uid');
          ssoParams.delete('ts');
          ssoParams.delete('sig');
          const q = ssoParams.toString();
          window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash);
        }
      } catch (e) {
        console.error('SSO処理エラー:', e);
      }

      try {
        const myGarageUser = await validateMyGarageToken();
        if (myGarageUser) {
          const aishaUser: User = {
            id: myGarageUser.id,
            name: myGarageUser.name,
            isAdmin: myGarageUser.isAdmin, // 🔧 管理者フラグを追加！
            loginType: 'mygarage', // 🔧 loginTypeを追加！
            personalSettings: myGarageUser.personalSettings || {
              numberManagement: {},
              referenceRegistration: {
                carPhotos: [
                  { viewAngle: 'front', label: 'フロント正面' },
                  { viewAngle: 'side', label: 'サイド' },
                  { viewAngle: 'rear', label: 'リア' },
                  { viewAngle: 'front_angled_7_3', label: 'フロント斜め' },
                  { viewAngle: 'rear_angled_7_3', label: 'リア斜め' }
                ]
              }
            }
          };
          
          setUser(aishaUser);
          setCurrentAppView('generator');
          return; // MyGarageでログイン成功したら終了
        }
      } catch (error) {
        console.error('MyGarage token validation error:', error);
      }

      // MyGarageでログインできなかった場合、AWS SMSクッキーを確認
      try {
        const cookies = document.cookie.split(';');
        let awsSmsUser = null;
        
        for (const cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'aws_sms_user' && value) {
            try {
              awsSmsUser = JSON.parse(decodeURIComponent(value));
              break;
            } catch (e) {
              console.error('Failed to parse AWS SMS cookie:', e);
            }
          }
        }
        
        if (awsSmsUser && awsSmsUser.id) {
          const aishaUser: User = {
            id: awsSmsUser.id,
            name: awsSmsUser.name || '',
            loginType: 'aws_sms',
            phoneNumber: awsSmsUser.phoneNumber,
            isAdmin: false,
          };
          
          setUser(aishaUser);
          setCurrentAppView('generator');
        }
      } catch (error) {
        console.error('AWS SMS cookie validation error:', error);
      }
    };

    checkLoginStatus();
    
    // 初回の公開タイムラインを読み込み
    loadPublicLibraryData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('share') === 'true') {
      const decodedImageUrl = decodeURIComponent(params.get('image') || '');
      setShareParams({
        sharedByUser: decodeURIComponent(
          params.get('user') || '不明なユーザー',
        ),
        sharedDate: decodeURIComponent(
          params.get('date') || new Date().toISOString(),
        ),
        sharedImageUrl: decodedImageUrl,
        sharedPrompt: decodeURIComponent(params.get('prompt') || ''),
        sharedMenuName: decodeURIComponent(params.get('menu') || ''),
      });
    }
  }, []);

  useEffect(() => {
    if (user) refreshCredits(user.id);
  }, [user]);

  // SuzuriGoodsHistoryItemをGoodsCreationRecordに変換する関数
  const convertToGoodsCreationRecord = (item: SuzuriGoodsHistoryItem): GoodsCreationRecord => {
    return {
      id: item.id.toString(),
      goodsName: item.product_title,
      imageId: item.library_image_id,
      imageUrl: item.sample_image_url, // SUZURI商品のプレビュー画像をサムネイルに使用
      prompt: item.description || item.car_name,
      timestamp: new Date(item.created_at),
      creditCost: 0, // APIレスポンスにない場合は0
      selectedVariations: {
        itemType: item.item_name,
        productId: item.product_id.toString(),
        productUrl: item.product_url,
        originalImageUrl: item.original_image_url, // 元画像URLはselectedVariationsに保存
      },
      price: item.price,
      display_price: item.display_price,
      has_order: item.has_order,   // Prodigi等の購入済フラグ
    };
  };

  // グッズ履歴を取得する関数（独立）
  const loadGoodsHistory = async () => {
    if (user?.id) {
      try {
        const historyData = await suzuriApiClient.getUserGoodsHistory(user.id);
        
        const convertedHistory = historyData.map(convertToGoodsCreationRecord);
        setGoodsCreationHistory(convertedHistory);
      } catch (error) {
        console.error('❌ グッズ履歴取得エラー:', error);
        // エラーの場合は空配列を設定
        setGoodsCreationHistory([]);
      }
    }
  };

  // グッズ履歴を取得するuseEffect
  useEffect(() => {
    if (showGoodsHistoryModal) {
      loadGoodsHistory();
    }
  }, [showGoodsHistoryModal, user?.id]);

  const toggleView = useCallback(() => {
    setIsAdminView((prev) => !prev);
    if (!isAdminView) setCurrentAppView('generator');
  }, [isAdminView]);

  const handleLogin = async () => {
    try {
      const myGarageUser = await myGarageLogin(inputLoginUsername, inputLoginPassword);
      
      if (myGarageUser) {
        // ユーザー名のみ保存（パスワードはブラウザ自動入力に任せる）
        if (saveLoginPassword) {
          localStorage.setItem('mygarage-saved-username', inputLoginUsername);
        } else {
          localStorage.removeItem('mygarage-saved-username');
        }
        localStorage.removeItem('mygarage-save-password');
        
        // MyGarageユーザーをAISHAのUser型に変換
        const aishaUser: User = {
          id: myGarageUser.id, // MyGarageから取得したユーザーID
          name: myGarageUser.name, // ← ここに取得したユーザー名を表示
          isAdmin: myGarageUser.isAdmin, // 🔧 管理者フラグを追加！
          personalSettings: myGarageUser.personalSettings || {
            numberManagement: {},
            referenceRegistration: {
              carPhotos: [
                { viewAngle: 'front', label: 'フロント正面' },
                { viewAngle: 'side', label: 'サイド' },
                { viewAngle: 'rear', label: 'リア' },
                { viewAngle: 'front_angled_7_3', label: 'フロント斜め' },
                { viewAngle: 'rear_angled_7_3', label: 'リア斜め' }
              ]
            }
          }
        };
        
        setUser(aishaUser);
        setShowLoginModal(false);
        setCurrentAppView('generator');
        showToast('success', `${myGarageUser.name}さん、ログインしました`);
      }
    } catch (error) {
      console.error('Login error:', error);
      showToast('error', error instanceof Error ? error.message : 'ログインエラーが発生しました');
    }
  };

  const handlePhoneLogin = () => {
    setShowLoginModal(false);
    setShowPhoneLoginModal(true);
  };

  const handleAwsSmsLogin = () => {
    setShowLoginModal(false);
    setShowAwsSmsLoginModal(true);
  };

  const handlePhoneLoginSuccess = (user: User) => {
    setUser(user);
    setShowPhoneLoginModal(false);
    setCurrentAppView('generator');
    showToast('success', 'ログインしました');
  };

  const handlePhoneLoginError = (message: string) => {
    showToast('error', message);
  };

  const handleAwsSmsLoginSuccess = (user: User) => {
    setUser(user);
    setShowAwsSmsLoginModal(false);
    setCurrentAppView('generator');
    showToast('success', 'AWS SMS認証でログインしました');
  };

  const handleAwsSmsLoginError = (message: string) => {
    showToast('error', message);
  };

  const handleLogout = async () => {
    try {
      // ログインタイプに応じてログアウト処理を実行
      if (user?.loginType === 'phone') {
        await phoneLogout();
      } else if (user?.loginType === 'aws_sms') {
        // AWS SMSのクッキーを削除
        document.cookie = 'aws_sms_user=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
      } else {
        await myGarageLogout();
      }
      setUser(null);
      setIsAdminView(false);
      setCurrentAppView('timeline');
      showToast('success', 'ログアウトしました');
    } catch (error) {
      console.error('Logout error:', error);
      // エラーでもログアウト状態にする
      setUser(null);
      setIsAdminView(false);
      setCurrentAppView('timeline');
      showToast('info', 'ログアウトしました');
    }
  };

  const handleSelectPlan = async (plan: Plan) => {
    if (!user) {
      showToast('error', 'ユーザー情報が取得できません');
      setShowPlanModal(false);
      return;
    }

    // Stripe決済は既にChargeOptionsModal内で完了しているため、
    // ここではクレジット残高の更新のみ行う
    refreshCredits(user.id);
    setShowPlanModal(false); // 最後にモーダルを閉じる
  };

  const addToGenerationHistory = useCallback(
    async (image: GeneratedImage) => {
      if (!user?.id) return;
      
      // 既にライブラリに保存済みかチェック
      const isAlreadySaved = generationHistory.some(
        (existingImage) => existingImage.id === image.id
      );
      
      if (isAlreadySaved) {
        showToast('info', 'この画像は既にライブラリに保存されています');
        return;
      }
      
      const imageWithAuthor = { ...image, authorName: user?.name || 'ゲスト' };
      
      try {
        // タイムラインAPIに保存（ライブラリフラグ=trueで保存）
        const savedImage = await saveToTimeline(user.id, { ...imageWithAuthor, isSavedToLibrary: true });
        if (savedImage) {
          // ローカルステートも更新
          setGenerationHistory((prev) => [
            savedImage,
            ...prev.filter((img) => img.id !== savedImage.id),
          ]);
          
          // 公開画像の場合は公開リストも更新
          if (savedImage.isPublic) {
            setAllPublicImages((prevPublic) => {
              const existingIndex = prevPublic.findIndex(
                (pImg) => pImg.id === savedImage.id,
              );
              if (existingIndex > -1) {
                const updatedPublic = [...prevPublic];
                updatedPublic[existingIndex] = savedImage;
                return updatedPublic;
              }
              return [savedImage, ...prevPublic];
            });
          } else {
            setAllPublicImages((prevPublic) =>
              prevPublic.filter((pImg) => pImg.id !== savedImage.id),
            );
          }
          
          showToast('success', 'ライブラリに保存されました');
        }
      } catch (error: any) {
        console.error('ライブラリ保存に失敗しました:', error);
        
        // 重複エラーの場合は特別なメッセージを表示
        if (error?.response?.status === 400 && 
            error?.response?.data?.error?.includes?.('unique') ||
            error?.response?.data?.error?.includes?.('重複')) {
          showToast('info', 'この画像は既にライブラリに保存されています');
        } else {
          showToast('error', 'ライブラリへの保存に失敗しました');
        }
        
        // エラーの場合はローカルステートのみ更新
        setGenerationHistory((prev) => [
          imageWithAuthor,
          ...prev.filter((img) => img.id !== imageWithAuthor.id),
        ]);
      }
    },
    [user, showToast],
  );

  // 画像生成時にタイムラインに自動保存（ライブラリフラグ=false）
  const saveToTimelineOnGeneration = useCallback(
    async (image: GeneratedImage) => {
      if (!user?.id) return;
      
      const imageWithAuthor = { ...image, authorName: user?.name || 'ゲスト' };
      
      try {
        // タイムラインAPIに保存（ライブラリフラグ=falseで保存）
        const savedImage = await saveToTimeline(user.id, { ...imageWithAuthor, isSavedToLibrary: false });
        if (savedImage) {
          // タイムラインに保存されました
        }
      } catch (error: any) {
        console.error('❌ タイムライン保存に失敗しました:', error);
        // タイムライン保存に失敗してもエラートーストは出さない（ユーザー体験を損なわないため）
      }
    },
    [user],
  );

  // 既存画像をライブラリに保存する専用関数
  const saveExistingImageToLibrary = useCallback(
    async (image: GeneratedImage) => {
      if (!user?.id) return;
      
      try {
        // 既存画像のライブラリフラグを更新
        const updatedImage = await updateTimelineEntry(user.id, image.id, { 
          isSavedToLibrary: true 
        });
        
        if (updatedImage) {
          // セッション画像のライブラリフラグを更新
          setGeneratedImages((prev) =>
            prev.map((img) => 
              img.id === image.id 
                ? { ...img, isSavedToLibrary: true }
                : img
            ),
          );
          
          // ライブラリ履歴を再読み込み
          loadLibraryData();
          
          showToast('success', 'ライブラリに保存されました');
        }
      } catch (error: any) {
        console.error('❌ ライブラリ保存に失敗しました:', error);
        showToast('error', 'ライブラリへの保存に失敗しました');
      }
    },
    [user, showToast],
  );

  const addToGoodsHistory = useCallback((record: GoodsCreationRecord) => {
    setGoodsCreationHistory((prev) => [record, ...prev]);
  }, []);

  const handleUpdateCredits = useCallback(
    (newCreditAmount: number) => {
      if (user) {
        setUser((prevUser) => ({ ...prevUser!, credits: newCreditAmount }));
      }
    },
    [user],
  );

  const handleLoadOptionsFromHistory = useCallback(
    (formData: MenuExecutionFormData, generatedImageUrl?: string) => {
      // ライブラリからの画像読み込み要求をactionAfterLoadとして設定
      setActionAfterLoad({
        type: 'loadFromLibrary',
        formData,
        generatedImageUrl,
      });
      setShowGenerationHistoryModal(false);
      setCurrentAppView('generator');
    },
    [],
  );

  const handleExtendImageFromLibrary = useCallback(
    (imageFromLibrary: GeneratedImage) => {
      if (!user || credits < EXTEND_IMAGE_CREDIT_COST) {
        showToast(
          'error',
          `画像を拡張するためのクレジットが不足しています。（必要: ${EXTEND_IMAGE_CREDIT_COST}, 保有: ${credits}）`,
        );
        return;
      }

      // 拡張方向選択モーダルを開く（ライブラリの詳細ビューは保持したまま）
      setImageToExpand(imageFromLibrary);
      setIsDirectionModalOpen(true);
      // ライブラリモーダルとビューはそのまま保持
    },
    [user, credits, showToast],
  );

  // 拡張方向選択モーダルのコールバック関数
  const handleDirectionModalClose = useCallback(() => {
    setIsDirectionModalOpen(false);
    setImageToExpand(null);
  }, []);

  const handleDirectionModalConfirm = useCallback(async (anchorPosition: AnchorPosition) => {
    if (!imageToExpand || !user) {
      showToast('error', '画像拡張の準備ができていません。');
      return;
    }

    // 拡張処理開始：ライブラリモーダルを閉じてジェネレータービューに切り替え
    setShowGenerationHistoryModal(false);
    setCurrentAppView('generator');
    setIsLibraryExtending(true); // ローディング開始

    try {
      // クレジット消費
      const reqBody = { 
        credits: EXTEND_IMAGE_CREDIT_COST,
        user_id: user.id 
      };
      const onError = () =>
        showToast('error', 'クレジット消費に失敗しました（画像拡張は成功）');
      await consumeCredits(reqBody, onError);
      refreshCredits(user.id);
      
      // 画像拡張API呼び出し
      const expandedImage = await expandImage(
        imageToExpand.id,
        anchorPosition,
        user.id,
        imageToExpand, // 元の画像データを送信
        (error) => {
          console.error('画像拡張エラー:', error);
          showToast('error', error instanceof Error ? error.message : '画像拡張に失敗しました');
        }
      );

      if (expandedImage) {
        // 生成履歴に追加（バックエンドで既にタイムラインに保存済み）
        setGenerationHistory(prev => [expandedImage, ...prev]);
        // ジェネレータービューの生成画像リストにも追加
        setGeneratedImages(prev => [expandedImage, ...prev]);
        
        showToast('success', '画像の拡張が完了しました！');
        // 既にジェネレータービューに切り替え済み
      }
    } catch (error) {
      console.error('画像拡張処理エラー:', error);
      showToast('error', '画像拡張処理中にエラーが発生しました');
    } finally {
      handleDirectionModalClose();
      setIsLibraryExtending(false); // ローディング終了
    }
  }, [imageToExpand, user, refreshCredits, showToast, setGenerationHistory, handleDirectionModalClose]);

  const handleActionAfterLoadPerformed = useCallback(() => {
    setActionAfterLoad(null);
  }, []);

  const handleRateImageInLibrary = useCallback(
    async (imageId: string, rating: 'good' | 'bad') => {
      if (!user?.id) return;
      
      try {
        // ライブラリAPIで評価を更新
        const updatedImage = await updateTimelineEntry(user.id, imageId, { rating });
        if (updatedImage) {
          // ローカルステートも更新
          setGenerationHistory((prev) =>
            prev.map((img) => (img.id === imageId ? { ...img, rating } : img)),
          );
          setAllPublicImages((prev) =>
            prev.map((img) => (img.id === imageId ? { ...img, rating } : img)),
          );
          // セッション画像にも反映
          setGeneratedImages((prev) =>
            prev.map((img) => (img.id === imageId ? { ...img, rating } : img)),
          );

          showToast(
            'info',
            `画像を評価しました: ${rating === 'good' ? '良い' : '悪い'}`,
          );
        }
      } catch (error) {
        console.error('評価の更新に失敗しました:', error);
        showToast('error', '評価の更新に失敗しました');
      }
    },
    [user, showToast],
  );

  const handleDeleteFromLibrary = useCallback(async (imageId: string) => {
    if (!user?.id) return;
    
    try {
      const success = await deleteFromTimeline(user.id, imageId);
      if (success) {
        setGenerationHistory((prev) => prev.filter((img) => img.id !== imageId));
        setAllPublicImages((prev) => prev.filter((img) => img.id !== imageId));
        showToast('info', `画像をライブラリから削除しました。`);
      }
    } catch (error) {
      console.error('削除に失敗しました:', error);
      showToast('error', '削除に失敗しました');
    }
  }, [user, showToast]);

  const handleCreateGoodsForLibrary = useCallback(
    (item: SuzuriItem, image: GeneratedImage) => {
      if (!user || credits < item.creditCost) {
        showToast(
          'error',
          `${item.name}を作成するためのクレジットが不足しています。`,
        );
        return;
      }
      const newBalance = credits - item.creditCost;
      handleUpdateCredits(newBalance);

      const goodsRecord: GoodsCreationRecord = {
        id: `${item.id}-${Date.now()}`,
        goodsName: item.name,
        imageId: image.id,
        imageUrl: image.url,
        prompt: image.displayPrompt,
        timestamp: new Date(),
        creditCost: item.creditCost,
      };
      addToGoodsHistory(goodsRecord);
      showToast(
        'success',
        `(モック) ${item.name}をライブラリの画像から作成しました。`,
      );
    },
    [user, handleUpdateCredits, addToGoodsHistory],
  );

  const handleToggleLibraryImagePublicStatus = useCallback(
    async (imageId: string, isPublic: boolean) => {
      if (!user?.id) return;
      
      try {
        const authorName = isPublic ? user?.name || 'ゲスト' : undefined;
        const updatedImageFromAPI = await updateTimelineEntry(user.id, imageId, { 
          isPublic, 
          authorName 
        });
        
        if (updatedImageFromAPI) {
          // ローカルステート更新
          setGenerationHistory((prev) =>
            prev.map((img) => 
              img.id === imageId 
                ? { ...img, isPublic, authorName }
                : img
            ),
          );

          // generatedImages 側も更新
          setGeneratedImages((prev) =>
            prev.map((img) =>
              img.id === imageId
                ? { ...img, isPublic, authorName }
                : img,
            ),
          );

          // 公開画像リスト更新
          if (isPublic) {
            setAllPublicImages((prevPublic) => {
              const existingIndex = prevPublic.findIndex(
                (pImg) => pImg.id === imageId,
              );
              if (existingIndex > -1) {
                const newPublicList = [...prevPublic];
                newPublicList[existingIndex] = { ...updatedImageFromAPI, authorName };
                return newPublicList;
              }
              return [{ ...updatedImageFromAPI, authorName }, ...prevPublic];
            });
          } else {
            setAllPublicImages((prevPublic) =>
              prevPublic.filter((pImg) => pImg.id !== imageId),
            );
          }

          showToast(
            'info',
            `画像の公開設定を${isPublic ? '公開' : '非公開'}に変更しました。`,
          );
        }
      } catch (error) {
        console.error('公開設定の更新に失敗しました:', error);
        showToast('error', '公開設定の更新に失敗しました');
      }
    },
    [user, showToast],
  );

  const handleCommentUpdate = useCallback(
    (imageId: string, newCommentCount: number) => {
      // ライブラリ画像のコメント数を更新
      setGenerationHistory(prevImages => 
        prevImages.map(prevImage => 
          prevImage.id === imageId 
            ? { ...prevImage, commentCount: newCommentCount, comment_count: newCommentCount }
            : prevImage
        )
      );
      
      // セッション画像にも反映
      setGeneratedImages(prevImages => 
        prevImages.map(prevImage => 
          prevImage.id === imageId 
            ? { ...prevImage, commentCount: newCommentCount, comment_count: newCommentCount }
            : prevImage
        )
      );
      
      // 公開画像リストにも反映
      setAllPublicImages(prevImages => 
        prevImages.map(prevImage => 
          prevImage.id === imageId 
            ? { ...prevImage, commentCount: newCommentCount, comment_count: newCommentCount }
            : prevImage
        )
      );
    },
    [],
  );

  const toggleAppViewMode = useCallback(() => {
    setCurrentAppView((prev) =>
      prev === 'generator' ? 'timeline' : 'generator',
    );
  }, []);

  const handleSavePersonalSettings = useCallback(
    (settings: PersonalUserSettings) => {
      if (user) {
        setUser((prevUser) => ({
          ...prevUser!,
          personalSettings: settings,
        }));
        showToast('success', '愛車設定を保存しました。');
        setShowPersonalSettingsModal(false);
      }
    },
    [user],
  );

  // 決済履歴モーダルのハンドラー
  const handlePaymentHistoryClick = useCallback(() => {
    setShowPaymentHistoryModal(true);
  }, [user]);

  if (shareParams && shareParams.sharedImageUrl) {
    return (
      <ShareView
        params={shareParams}
        onClose={() => {
          setShareParams(null);
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        }}
      />
    );
  }

  const renderMainContent = () => {
    // 認証チェック中はローディング表示
    if (isAuthChecking) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-400 mx-auto mb-4"></div>
            <p className="text-gray-400">読み込み中...</p>
          </div>
        </div>
      );
    }
    // ログインしていない＆menuIdFromUrlがある場合 → MenuLandingPageを表示
    if (!user && menuIdFromUrl) {
      return <MenuLandingPage />;
    }
    if (!user) {
      return (
        <PublicTimelineView publicImages={allPublicImages} currentUser={null} />
      );
    }
    if (isAdminView) {
      return <AdminView />;
    }
    // ログイン済み＆メニューURLモードの場合 → メニュー説明 + 生成パネル + タイムライン
    if (currentAppView === 'timeline' && isMenuUrlMode && menuFromUrl) {
      // メニューURLモード用のformData（menuFromUrlを固定で設定）
      const menuUrlFormData: MenuExecutionFormData = {
        ...menuExePanelFormData,
        menu: menuFromUrl,
      };

      const handleGenerateFromMenuUrl = (formData: MenuExecutionFormData) => {
        // formDataを保持してUserViewに遷移し、生成を開始
        setMenuExePanelFormData(formData);
        setCurrentAppView(null); // UserViewに切り替え
        // UserViewに遷移後、自動で生成を開始するためのフラグをセット
        setActionAfterLoad('generate');
      };

      return (
        <div className="space-y-6">
          {/* 2カラムレイアウト：メニュー説明 + 生成パネル */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* 左側：メニュー説明 */}
            <div className="lg:w-1/2 bg-gradient-to-r from-indigo-900/50 to-purple-900/50 rounded-lg p-6 border border-indigo-700/30">
              <h2 className="text-2xl font-bold text-white mb-2">{menuFromUrl.name}</h2>
              {menuFromUrl.description && (
                <p className="text-gray-300 leading-relaxed">{menuFromUrl.description}</p>
              )}
              {/* 使い方説明画像 */}
              {(menuFromUrl.usageGuideImageUrl || menuFromUrl.usage_guide_image_url) && (
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-2">使い方</p>
                  <img
                    src={menuFromUrl.usageGuideImageUrl || menuFromUrl.usage_guide_image_url}
                    alt="使い方説明"
                    className="max-w-lg w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
              )}
              {/* サンプル画像 */}
              {(menuFromUrl.sampleInputImgUrl || menuFromUrl.sampleResultImgUrl) && (
                <div className="mt-4 flex flex-wrap gap-6">
                  {menuFromUrl.sampleInputImgUrl && (
                    <div className="flex flex-col items-center">
                      <p className="text-sm text-gray-400 mb-2">入力画像例</p>
                      <img
                        src={menuFromUrl.sampleInputImgUrl}
                        alt="サンプル入力"
                        className="max-w-xs h-auto rounded-lg shadow-lg"
                      />
                    </div>
                  )}
                  {menuFromUrl.sampleResultImgUrl && (
                    <div className="flex flex-col items-center">
                      <p className="text-sm text-gray-400 mb-2">生成結果例</p>
                      <img
                        src={menuFromUrl.sampleResultImgUrl}
                        alt="サンプル結果"
                        className="max-w-xs h-auto rounded-lg shadow-lg"
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 flex items-center gap-2">
                <span className="text-gray-400 text-sm">消費クレジット:</span>
                <span className="text-lg font-bold text-yellow-400">{menuFromUrl.credit}</span>
              </div>
            </div>

            {/* 右側：生成パネル */}
            <div className="lg:w-1/2 h-[calc(100vh-200px)] overflow-y-auto">
              <MenuExecutionPanel
                onGenerateClick={handleGenerateFromMenuUrl}
                isGenerating={false}
                formData={menuUrlFormData}
                setFormData={(updater) => {
                  if (typeof updater === 'function') {
                    setMenuExePanelFormData(prev => ({
                      ...updater(prev),
                      menu: menuFromUrl, // メニューは固定
                    }));
                  } else {
                    setMenuExePanelFormData({
                      ...updater,
                      menu: menuFromUrl, // メニューは固定
                    });
                  }
                }}
                currentUser={user}
                isMenuLocked={true}
              />
            </div>
          </div>

          {/* フィルタリングされた公開タイムライン */}
          <h3 className="text-xl font-bold text-indigo-400">
            「{menuFromUrl.name}」で作成された作品
          </h3>
          <PublicTimelineView
            publicImages={filteredPublicImages}
            currentUser={user}
            hideTitle
          />
        </div>
      );
    }
    if (currentAppView === 'timeline') {
      return (
        <PublicTimelineView publicImages={allPublicImages} currentUser={user} />
      );
    }
    return (
      <UserView
        currentUser={user} // Pass full user object
        addToGenerationHistory={addToGenerationHistory}
        saveToTimelineOnGeneration={saveToTimelineOnGeneration}
        saveExistingImageToLibrary={saveExistingImageToLibrary}
        onAddToGoodsHistory={addToGoodsHistory}
        onUpdateCredits={handleUpdateCredits}
        menuExePanelFormData={menuExePanelFormData}
        setMenuExePanelFormData={setMenuExePanelFormData}
        generatedImages={generatedImages}
        setGeneratedImages={setGeneratedImages}
        onToggleImagePublicStatus={handleToggleLibraryImagePublicStatus}
        onRateImage={handleRateImageInLibrary}
        onReloadUserHistory={loadUserGenerationHistory}
        isLibraryExtending={isLibraryExtending} // ライブラリ拡張中の状態を渡す
        actionAfterLoad={actionAfterLoad} // ライブラリからの読み込み処理を渡す
        onActionAfterLoadPerformed={handleActionAfterLoadPerformed} // 処理完了コールバック
      />
    );
  };

  return (
    <ChargeOptionsProvider>
      <div className="min-h-screen flex flex-col bg-gray-900 text-gray-100">
        {!isEmbed && (
        <Header
          user={user}
          onLoginClick={() => setShowLoginModal(true)}
          onLogoutClick={handleLogout}
          onPlansClick={() => setShowPlanModal(true)}
          onToggleAdminView={toggleView}
          isAdminView={isAdminView}
          onGenerationHistoryClick={() => setShowGenerationHistoryModal(true)}
          onGoodsHistoryClick={() => setShowGoodsHistoryModal(true)}
          currentAppView={user ? currentAppView : undefined}
          onToggleAppViewMode={user ? toggleAppViewMode : undefined}
          onPersonalSettingsClick={
            user ? () => setShowPersonalSettingsModal(true) : undefined
          }
          onInformationClick={() => window.location.href = '/information'}
        />
        )}
        <main className={`flex-grow container mx-auto px-4 ${isEmbed ? 'py-3' : 'py-8'}`}>
          <CategoriesProvider>
            <MenusProvider>
              <MasterReferencesProvider>
                {renderMainContent()}
              </MasterReferencesProvider>
            </MenusProvider>
          </CategoriesProvider>
        </main>

        {showLoginModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-md">
              <h2 className="text-2xl font-semibold mb-6 text-center text-indigo-400">
                MyGarage ログイン
              </h2>
              <div className="mb-4">
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  メールアドレス
                </label>
                <input
                  type="email"
                  id="username"
                  name="username"
                  placeholder="MyGarageのメールアドレス"
                  className="w-full p-2.5 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-500 focus:ring-indigo-500 focus:border-indigo-500"
                  value={inputLoginUsername}
                  onChange={(e) => setInputLoginUsername(e.target.value)}
                />
              </div>
              <div className="mb-6">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  パスワード
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="MyGarageのパスワード"
                  className="w-full p-2.5 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-500 focus:ring-indigo-500 focus:border-indigo-500"
                  value={inputLoginPassword}
                  onChange={(e) => setInputLoginPassword(e.target.value)}
                />
              </div>
              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="savePassword"
                  checked={saveLoginPassword}
                  onChange={(e) => setSaveLoginPassword(e.target.checked)}
                  className="mr-2 h-4 w-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                />
                <label htmlFor="savePassword" className="text-sm text-gray-300">
                  ログインパスワードを保存する
                </label>
              </div>
              <p className="text-gray-400 mb-4 text-center text-xs">
                MyGarageアカウントのメールアドレスでログインします
              </p>
              <button
                onClick={handleLogin}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg transition duration-150 ease-in-out mb-3"
              >
                MyGarageでログイン
              </button>
              
              <div className="relative mb-3">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-gray-800 px-2 text-gray-500">開発用</span>
                </div>
              </div>
              
              <button
                onClick={handleAwsSmsLogin}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-lg transition duration-150 ease-in-out mb-3"
              >
                電話番号でログイン (AWS SMS)
              </button>
              
              <button
                onClick={() => setShowLoginModal(false)}
                className="w-full bg-gray-600 hover:bg-gray-700 text-gray-200 font-bold py-3 px-6 rounded-lg transition duration-150 ease-in-out"
              >
                閉じる
              </button>
            </div>
          </div>
        )}

        <ChargeOptionsModal
          isOpen={showPlanModal}
          onClose={() => setShowPlanModal(false)}
          onSelectPlan={handleSelectPlan}
          currentUser={user}
          onPaymentHistoryClick={handlePaymentHistoryClick}
        />

        <GenerationHistoryModal
          isOpen={showGenerationHistoryModal}
          onClose={() => setShowGenerationHistoryModal(false)}
          history={generationHistory}
          onLoadOptions={handleLoadOptionsFromHistory}
          onRateImageInLibrary={handleRateImageInLibrary}
          onDeleteFromLibrary={handleDeleteFromLibrary}
          onCreateGoodsForLibrary={handleCreateGoodsForLibrary}
          onExtendImageFromLibrary={handleExtendImageFromLibrary}
          onToggleLibraryImagePublicStatus={
            handleToggleLibraryImagePublicStatus
          }
          onCommentUpdate={handleCommentUpdate}
          currentUser={user}
        />
        <GoodsCreationHistoryModal
          isOpen={showGoodsHistoryModal}
          onClose={() => setShowGoodsHistoryModal(false)}
          history={goodsCreationHistory}
          currentUser={user}
          onGoodsCreated={() => {
            loadGoodsHistory();
          }}
        />
        
        {/* 決済履歴モーダル */}
        <PaymentHistoryModal
          isOpen={showPaymentHistoryModal}
          onClose={() => setShowPaymentHistoryModal(false)}
          currentUser={user}
        />
        
        {/* 拡張方向選択モーダル */}
        <DirectionSelectionModal
          isOpen={isDirectionModalOpen}
          onClose={handleDirectionModalClose}
          onConfirm={handleDirectionModalConfirm}
          imageName={imageToExpand?.displayPrompt || '画像'}
        />
        {user &&
          showPersonalSettingsModal && ( // 参照設定モーダル
            <ReferenceSettingsView
              currentUser={user}
              onSave={handleSavePersonalSettings}
              onClose={() => setShowPersonalSettingsModal(false)}
            />
          )}
        
        {/* AWS SMS認証ログインモーダル */}
        <AwsSmsLoginModal
          isOpen={showAwsSmsLoginModal}
          onClose={() => setShowAwsSmsLoginModal(false)}
          onLoginSuccess={handleAwsSmsLoginSuccess}
          onError={handleAwsSmsLoginError}
        />
        
        <ToastNotification />
      </div>
    </ChargeOptionsProvider>
  );
};

export default App;
